import React, { useEffect, useState } from 'react';
import { db } from '../../firebase-config';
import {
  collection, getDocs, deleteDoc, doc,
  updateDoc, addDoc, serverTimestamp
} from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase-config';
import { FaBriefcase, FaFileCsv } from 'react-icons/fa';
import { BsFillPersonFill } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import DeleteModal from '../DeleteModal';
import Papa from 'papaparse';
import { ulid } from 'ulid';                      // <── NEW

export default function MyLists() {
  const [user] = useAuthState(auth);
  const [searches, setSearches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [editingName, setEditingName] = useState({});

  /* ───────────────────────────── fetch once on mount ───────────────────────────── */
  useEffect(() => {
    (async () => {
      if (!user) {
        const cached = JSON.parse(localStorage.getItem('searches')) ?? [];
        setSearches(cached.map(s => ({ ...s, createdAt: new Date(s.createdAt) })));
        return;
      }

      const [bizSnap, pplSnap, csvSnap] = await Promise.all([
        getDocs(collection(db, 'searches', user.uid, 'businessSearches')),
        getDocs(collection(db, 'searches', user.uid, 'peopleSearches')),
        getDocs(collection(db, 'searches', user.uid, 'csvSearches'))
      ]);

      const fmtDate = d => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

      const map = (snap, type) => snap.docs.map(d => {
        const data = d.data();
        const createdAt = data.createdAt?.toDate?.() ?? new Date();
        return {
          id      : d.id,
          ...data,
          createdAt,
          type,
          name    : data.name || `${fmtDate(createdAt)} – ${type}`,
          userId  : user.uid
        };
      });

      const combined = [...map(bizSnap, 'Business'),
                        ...map(pplSnap, 'People'),
                        ...map(csvSnap, 'CSV')]
                      .sort((a,b) => b.createdAt - a.createdAt);

      setSearches(combined);
      localStorage.setItem('searches', JSON.stringify(combined));
    })();
  }, [user]);

  /* ───────────────────────────── helpers ───────────────────────────── */
  const confirmDelete = (id, type) => { setDeleteInfo({ id, type }); setShowModal(true); };

  const handleDelete = async (id, type) => {
    const coll = type === 'Business' ? 'businessSearches'
               : type === 'People'   ? 'peopleSearches'
               :                       'csvSearches';

    await deleteDoc(doc(db, 'searches', user.uid, coll, id));
    const upd = searches.filter(s => s.id !== id);
    setSearches(upd);
    localStorage.setItem('searches', JSON.stringify(upd));
    setShowModal(false);
  };

  const handleNameBlur = async (id, type) => {
    const newName = editingName[id] ?? '';
    const coll    = type === 'Business' ? 'businessSearches'
                  : type === 'People'   ? 'peopleSearches'
                  :                       'csvSearches';

    await updateDoc(doc(db, 'searches', user.uid, coll, id), { name: newName });
    const upd = searches.map(s => s.id === id ? { ...s, name: newName } : s);
    setSearches(upd);
    localStorage.setItem('searches', JSON.stringify(upd));
    setEditingName(p => ({ ...p, [id]: undefined }));
  };

  /* ───────────────────────────── CSV import ───────────────────────────── */
  const handleFileUpload = e => {
    const file = e.target.files[0];
    if (!file || !user) return;

    Papa.parse(file, {
      header: true,
      complete: async res => {
        const rows = res.data
          .filter(r => Object.values(r).some(Boolean))        // drop blank lines
          .map(r => ({
            id     : ulid(),          // insure stable row-id
            ...r,
            checked: false,
            notes  : '',
            tags   : []
          }));

        const newDoc = {
          name     : file.name,
          createdAt: serverTimestamp(),
          type     : 'CSV',
          results  : rows,
          userId   : user.uid
        };

        const ref = await addDoc(collection(db, 'searches', user.uid, 'csvSearches'), newDoc);

        setSearches(s => [{ id: ref.id, ...newDoc, createdAt: new Date() }, ...s]);
        localStorage.setItem('searches', JSON.stringify([{ id: ref.id, ...newDoc, createdAt: new Date() }, ...searches]));
      },
      error: err => console.error('CSV parse error:', err)
    });
  };

  /* ───────────────────────────── UI ───────────────────────────── */
  return (
    <div className="my-lists-container">
      <h1>My Lists</h1>

      <div className="options">
        <input type="file" accept=".csv" onChange={handleFileUpload} />

        {searches.length === 0
          ? <p>No searches found.</p>
          : <div className="lists-container">
              {searches.map(search => (
                <div key={search.id} className="list-item">
                  <div className="search-details">
                    {search.type === 'Business'
                      ? <FaBriefcase/>
                      : search.type === 'People'
                        ? <BsFillPersonFill/>
                        : <FaFileCsv/>}

                    <p><strong>{search.type}</strong> · {search.createdAt.toLocaleDateString(undefined,{month:'short',day:'numeric'})}</p>

                    <input
                      type="text"
                      value={editingName[search.id] ?? search.name}
                      onChange={e => setEditingName(p => ({ ...p, [search.id]: e.target.value }))}
                      onBlur={() => handleNameBlur(search.id, search.type)}
                    />

                    <Link to={`/list-viewer/${search.type.toLowerCase()}/${search.id}`}
                          state={{ search }}>View</Link>
                  </div>

                  <button onClick={() => confirmDelete(search.id, search.type)}>Delete</button>
                </div>
              ))}
            </div>}
      </div>

      <DeleteModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={() => handleDelete(deleteInfo.id, deleteInfo.type)}
      />
    </div>
  );
}